import { createApi } from "@reduxjs/toolkit/query/react";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { getCookie } from "@/hooks/cookie-helper";

const BaseURL = import.meta.env.VITE_SSO_BASE_URL;
export const SSO_APP_ID = import.meta.env.VITE_SSO_APP_ID;
const APP_PUBLIC_KEY = import.meta.env.VITE_SSO_APP_PUBLIC_KEY;

export const getTokenKey = () => {
  return `bslsso-${SSO_APP_ID}`;
};

export const setSsoToken = (token: string) => {
  localStorage.setItem(getTokenKey(), token);
};

const getSsoToken = () => {
  const isServerSide = typeof document === "undefined";
  if (!isServerSide) return localStorage.getItem(getTokenKey());
};

const getHeaders = () => {
  const myHeaders = new Headers();
  myHeaders.append("x-sso-appId", SSO_APP_ID);
  myHeaders.append("x-sso-public", APP_PUBLIC_KEY);
  myHeaders.append("Content-Type", "application/json");
  return myHeaders;
};

const getRequestOptions = () => {
  const requestOptions: any = {
    method: "GET",
    headers: getHeaders(),
  };
  return requestOptions;
};

export const loadSsoAppConfig = async () => {
  return await fetch(
    `${BaseURL}/api/v1/app/auth/retrieve-app-config`,
    getRequestOptions()
  );
};

export const getAuthenticatedSSOUser = async (cookie: string | null) => {
  const newOptions = { ...getRequestOptions() };

  const header = newOptions["headers"];
  header.append("cookie", cookie);
  if (cookie)
    header.append(
      "Authorization",
      `Bearer ${getCookie(getTokenKey(), cookie)}`
    );

  newOptions["credentials"] = "include";
  newOptions["headers"] = header;

  return await fetch(`${BaseURL}/api/v1/app/auth/me`, newOptions);
};

const createBaseQuery = (path: string = "") => {
  const token = getSsoToken();
  return fetchBaseQuery({
    baseUrl: `${BaseURL}${path}`,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("content-type", "application/json");
      headers.set("accept", "*");
      headers.set("x-sso-appId", SSO_APP_ID);
      headers.set("x-sso-public", APP_PUBLIC_KEY);
      if (token) headers.set("Authorization", `Bearer ${token}`);

      return headers;
    },
  });
};

export const ssoApi = createApi({
  reducerPath: "ssoApi",
  baseQuery: createBaseQuery("/api/v1/app/auth/"),
  tagTypes: ["ssoAuth"],
  endpoints: (build) => ({
    login: build.mutation<SSOLoginResponse, SSOLoginPayload>({
      query: (body) => ({
        url: "login",
        method: "POST",
        body,
      }),
    }),

    completeMfa: build.mutation<SSOCompleteMfaResponse, SSOCompleteMfaPayload>({
      query: (body) => ({
        url: "complete-mfa",
        method: "POST",
        body,
      }),
    }),

    changePassword: build.mutation<
      SSOChangePasswordResponse,
      SSOChangePasswordPayload
    >({
      query: (body) => ({
        url: "change-password",
        method: "PUT",
        body,
      }),
    }),

    editUser: build.mutation<SSOEditUserResponse, SSOEditUserPayload>({
      query: (body) => ({
        url: "edit-account",
        method: "PUT",
        body,
      }),
    }),

    authenticatedUser: build.query<SSOUser, void>({
      query: () => ({
        url: "me",
        method: "GET",
      }),
    }),

    // Create SSO Account
    createSSOAccount: build.mutation<
      SSOCreateAccountResponse,
      SSOCreateAccountPayload
    >({
      query: (body) => ({
        url: "register",
        method: "POST",
        body,
      }),
    }),

    // Resend Verification
    resendVerification: build.mutation<
      SSOResendVerificationResponse,
      SSOResendVerificationPayload
    >({
      query: ({ username, password, verification_type }) => ({
        url: `resend-verification/${verification_type}`,
        method: "POST",
        body: { username, password, otp: "123456" },
      }),
    }),

    // Complete Verification
    completeVerification: build.mutation<
      SSOCompleteVerificationResponse,
      SSOCompleteVerificationPayload
    >({
      query: (body) => ({
        url: "verify",
        method: "POST",
        body,
      }),
    }),

    // Retrieve Application Configurations
    retrieveAppConfig: build.query<SSOApp, void>({
      query: () => ({
        url: "retrieve-app-config",
        method: "GET",
      }),
    }),

    registerTotpDevice: build.mutation<
      SSORegisterTotpDeviceResponse,
      SSORegisterTotpDevicePayload
    >({
      query: (body) => ({
        url: "register-device",
        method: "POST",
        body,
      }),
    }),
  }),
});
